import { useMe } from "@/api/services/admin/auth";
import {
  createMessageApi,
  deleteMessageApi,
  setReadMessageApi,
  useConversation,
  useMessageListByConversation,
} from "@/api/services/admin/conversation";
import { TGetMessageOutputDto } from "@/api/services/admin/conversation/dtos/get-message-list-by-conversation.dto";
import { CHANNELS, EVENTS } from "@/constants/socket";
import { TMessageBoxProps } from "@/features/chat/components/MessageBox";
import { useSocket } from "@/providers/SocketProvider";
import { AppRouteNames } from "@/routes/constants";
import { toNumber } from "@/utils/number";
import { findLabelByValue } from "@/utils/object";
import { isFireEvent } from "@/utils/pieces";
import { replaceStr } from "@/utils/string";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

export const useRoom = () => {
  const { socket } = useSocket();
  const { data: meResult } = useMe();
  const { data: conversationResult } = useConversation();
  const { id } = useParams();

  const roomRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (roomRef.current) {
      roomRef.current.scrollTop = roomRef.current.scrollHeight;
    }
  };

  const roomData = conversationResult?.data;
  const { dataFormatter: messages, hasNextPage, isFetching, fetchNextPage } = useMessageListByConversation({ enabled: id ? true : false });
  const [oldMessages, setOldMessages] = useState<TMessageBoxProps[]>([]);
  const [newMessages, setNewMessages] = useState<TMessageBoxProps[]>([]);

  useEffect(() => {
    setNewMessages([]);
  }, [id]);

  useEffect(() => {
    const fetchOldMessages = async () => {
      const oldMsgs =
        messages && Array.isArray(messages) && messages.length > 0
          ? await Promise.all(
              messages.reverse().flatMap(async (message) => {
                return formatMsgObj(message);
              }),
            )
          : [];

      setOldMessages(oldMsgs.filter((oldMsg) => !newMessages.some((newMsg) => toNumber(newMsg.id) === toNumber(oldMsg.id))));
    };
    fetchOldMessages();
  }, [messages]);

  useEffect(() => {
    if (socket && roomData?.id) {
      socket.emit(EVENTS.CONVERSATION.JOIN_ROOM, replaceStr(CHANNELS.CONVERSATION.ROOM, { conversationId: roomData.id }));
      socket.on(EVENTS.CONVERSATION.READ_MESSAGE, (data) => {
        if (meResult?.data.id && data.readUserId != meResult.data.id) {
          setOldMessages((prevMessages) => prevMessages.map((msg) => (msg.position === "right" ? { ...msg, status: "received" } : msg)));
          setNewMessages((prevMessages) => prevMessages.map((msg) => (msg.position === "right" ? { ...msg, status: "received" } : msg)));
        }
      });
      socket.on(EVENTS.CONVERSATION.DELETE_MESSAGE, (data) => {
        setOldMessages((prevMessages) => prevMessages.filter((msg) => toNumber(msg.id) !== toNumber(data.messageId)));
        setNewMessages((prevMessages) => prevMessages.filter((msg) => toNumber(msg.id) !== toNumber(data.messageId)));
      });
      return () => {
        socket.emit(EVENTS.CONVERSATION.LEAVE, replaceStr(CHANNELS.CONVERSATION.ROOM, { conversationId: roomData.id }));
      };
    }
  }, [socket, roomData]);

  useEffect(() => {
    const callback = async (e: Event) => {
      if (isFireEvent<TGetMessageOutputDto>(e)) {
        const { detail: receivedMessage } = e;
        if (roomData && receivedMessage.conversationId.toString() === roomData.id.toString()) {
          let newMessage: TMessageBoxProps[] = [];
          newMessage = [await formatMsgObj(receivedMessage)];
          setNewMessages((cur) => [...cur, ...newMessage].filter((newMsg) => !oldMessages.some((oldMsg) => oldMsg.id === newMsg.id)));
          setTimeout(() => {
            scrollToBottom();
          }, 100);
        }
      }
    };
    window.addEventListener(EVENTS.CONVERSATION.NEW_MESSAGE, callback);
    return () => {
      window.removeEventListener(EVENTS.CONVERSATION.NEW_MESSAGE, callback);
    };
  }, [meResult, roomData]);

  const formatMsgObj = useCallback(
    async (msgObj: TGetMessageOutputDto): Promise<TMessageBoxProps> => {
      const routeOptions = Object.values(AppRouteNames);
      const screenRouteName = findLabelByValue(routeOptions, msgObj.screenRouteName) ?? "";
      const screenRouteURL = msgObj.screenRouteURL ?? null;
      const isMyMsg = meResult?.data.id == msgObj.creator?.id;
      const timer = dayjs(msgObj.createdAt).format("HH:mm");
      const msg = {} as TMessageBoxProps;
      msg.title = isMyMsg ? "" : msgObj.creator?.fullName;
      msg.messageText = msgObj.text;
      msg.position = isMyMsg ? "right" : "left";

      msg.files = msgObj.files;
      msg.statusTitle = isMyMsg ? "" : `${screenRouteName ? `(${screenRouteName})` : ""}`;
      msg.statusTitleURL = screenRouteURL;
      msg.timer = timer;
      msg.id = msgObj.id;
      msg.createdAt = msgObj.createdAt;

      if (isMyMsg) {
        if (meResult?.data.id && msgObj.readUserIds && msgObj.readUserIds.length > 0 && !msgObj.readUserIds.includes(meResult?.data.id)) {
          msg.status = "received";
        } else {
          msg.status = "sent";
        }
      }

      return msg;
    },
    [meResult?.data.id],
  );

  const sendMsg = async (msg: string, filePaths: string[]) => {
    if (!roomData) return;
    await createMessageApi(roomData.id.toString(), { text: msg, filePaths });
    scrollToBottom();
  };

  const readMsg = async () => {
    if (roomData) {
      await setReadMessageApi({ conversationId: roomData?.id.toString(), messageId: oldMessages[oldMessages.length - 1]?.id.toString() });
    }
  };

  const deleteMsg = async (id: number) => {
    if (roomData) {
      await deleteMessageApi({ conversationId: roomData.id?.toString(), messageId: id.toString() });
    }
  };

  return {
    oldMessages,
    newMessages,
    roomData: roomData ? roomData : null,
    hasNextPage,
    isFetching,
    fetchNextPage,
    sendMsg,
    readMsg,
    deleteMsg,
    roomRef,
    scrollToBottom,
  };
};
